/**
 * Crash hotspots: five years of crashes as a density surface, resolving into
 * individual crashes when you zoom in.
 *
 * Ported from TITAN_dev (components/MapLayers/Crashes/CrashHeatLayer.tsx) with
 * its tuning intact -- the numbers below are derived, not eyeballed, and a
 * hotspot has to read the same in both apps.
 *
 * Data: /v1/crashes/heat/{z}/{x}/{y}.pbf, built from crash_heat. The API
 * changes resolution with zoom (~4 km cells statewide, ~1 km regionally,
 * ~200 m in metros, individual crashes from z14) but every band emits the same
 * `n` (crashes) and `sw` (severity-weighted) properties.
 */

export const CRASH_HEAT_TILES =
    'https://api.missouri-titan.com/v1/crashes/heat/{z}/{x}/{y}.pbf';

export const CRASH_HEAT_ID = 'crash-heat';
export const CRASH_POINTS_ID = 'crash-heat-points';

// The value that reaches the top of the ramp: the p99 of the band the API
// serves at that zoom, so the colours span the real distribution instead of
// being pinned by two downtowns.
const SAT = {b0: 1847, b1: 310, b2: 64, raw: 25};

// Magma: sequential and perceptually uniform, and deliberately not the
// green-to-red used for live congestion -- a second red-on-dark ramp meaning
// five years of history would be read as congestion at a glance. It also
// varies in lightness end to end, so it survives red-green colour blindness.
const RAMP = {
    lo: 'rgba(59,15,112,0)',
    p1: 'rgba(59,15,112,0.55)',
    p2: 'rgba(140,41,129,0.72)',
    p3: 'rgba(222,73,104,0.85)',
    p4: 'rgba(254,159,109,0.92)',
    hi: 'rgba(252,253,191,0.96)',
};

/** Brightest is worst, matching the ramp the points resolve out of. */
const SEV_COLOR = ['#8c2981', '#de4968', '#fe9f6d', '#fcfdbf'];

export const CRASH_SEVERITY: Record<number, string> = {
    1: 'Property damage only',
    3: 'Minor injury',
    4: 'Serious injury',
    5: 'Fatal',
};

// sqrt-compressed and clamped: crash density is heavily skewed (the busiest
// 4 km cell holds ten thousand crashes against a median of seven), so a linear
// weight would render the state black except for two downtowns. One expression
// per band, stepped rather than interpolated so the switch lands exactly where
// the API hands over instead of blending two normalisations.
const norm = (max: number) => ['min', 1, ['/', ['sqrt', ['get', 'n']], Math.sqrt(max)]];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const crashHeatPaint: any = {
    'heatmap-weight': [
        'step', ['zoom'],
        norm(SAT.b0),
        8, norm(SAT.b1),
        10, norm(SAT.b2),
        14, norm(SAT.raw),
    ],
    // Derived from mapbox's own kernel rather than by eye: the kernel
    // integrates to 0.2754*r^2, so over a lattice of spacing s the density
    // settles at weight * intensity * 0.2754 * (r/s)^2, and intensity wants to
    // be its inverse. At the r ~= 2s held below that is ~0.85 and stays flat
    // through the band handovers; z4-5 and z13 are the exceptions, where the
    // radius floor and the finest grid respectively break that assumption.
    'heatmap-intensity': [
        'interpolate', ['linear'], ['zoom'],
        4, 0.16, 5, 0.32, 6, 0.85, 12, 0.9, 13, 1.6, 14, 0.9,
    ],
    'heatmap-color': [
        'interpolate', ['linear'], ['heatmap-density'],
        0, RAMP.lo, 0.12, RAMP.p1, 0.32, RAMP.p2,
        0.55, RAMP.p3, 0.78, RAMP.p4, 1, RAMP.hi,
    ],
    // ~2x the cell spacing at each zoom; the drops at z8 and z10 are the band
    // handovers, where the grid gets four to five times finer.
    'heatmap-radius': [
        'interpolate', ['linear'], ['zoom'],
        4, 5, 6, 9, 7, 17, 8, 9, 9, 17, 10, 7,
        11, 14, 12, 27, 13, 40, 14, 18, 16, 24,
    ],
    // Hand over to the individual crashes rather than stacking both: past z14 a
    // blur is strictly less informative than the points it stood in for.
    'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 13.5, 0.9, 15.5, 0],
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const crashPointPaint: any = {
    'circle-radius': [
        'interpolate', ['linear'], ['zoom'],
        14, ['case', ['>=', ['get', 'sev'], 4], 5, 3],
        17, ['case', ['>=', ['get', 'sev'], 4], 9, 6],
    ],
    'circle-color': [
        'match', ['get', 'sev'],
        5, SEV_COLOR[3], 4, SEV_COLOR[2], 3, SEV_COLOR[1], SEV_COLOR[0],
    ],
    // An outline is a second channel for the worst crashes, so severity does
    // not rest on colour alone.
    'circle-stroke-width': ['case', ['>=', ['get', 'sev'], 4], 1.5, 0],
    'circle-stroke-color': '#ffffff',
    'circle-opacity': ['interpolate', ['linear'], ['zoom'], 13.5, 0, 15, 0.9],
    'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 13.5, 0, 15, 0.85],
};

/** 14 -> "2 PM". The source clock is local crash time. */
export function crashHour(hr?: number): string | null {
    if (hr === undefined || hr === null) {
        return null;
    }
    const suffix = hr < 12 ? 'AM' : 'PM';
    return `${hr % 12 === 0 ? 12 : hr % 12} ${suffix}`;
}

/**
 * Crashes happening right now, from /v1/crashes/active.geojson. Drawn on top
 * of the historical surface: one is what to do something about, the other is
 * where it keeps happening.
 */
export const LIVE_CRASH_ID = 'crash-live';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const liveCrashPaint: any = {
    // Bigger than the historical points and constant with zoom, so an open
    // crash is findable at the zoom where you are looking at the whole state.
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 6, 12, 11, 16, 15],
    'circle-color': '#f44336',
    'circle-opacity': 0.75,
    'circle-stroke-width': 2,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-opacity': 0.9,
};
